"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import { useRouter, usePathname } from "next/navigation";

// Default preferences
const defaultPreferences = {
  theme: "auto",
  position: "bottom-right",
  size: "normal",
  opacity: 1,
  autoMinimize: false,
  showNotifications: true,
  enableSounds: false,
  enableVoice: false,
  contextAwareness: true,
  proactiveSuggestions: true,
  shareAnalytics: false,
  animationSpeed: "normal",
  keyboardShortcuts: true,
};

// Default state
// NOTE: Message state is managed by useChatStore (Zustand).
// This context only manages floating window UI state (position, size, etc.).
const defaultState = {
  isOpen: false,
  isMinimized: false,
  isMaximized: false,
  position: { x: -420, y: -620 }, // Bottom-right offset
  size: { width: 400, height: 600 },
  isDragging: false,
  isResizing: false,
  transport: null, // Reference to the SOLA transport instance
  currentPage: {
    route: "/",
    pageType: "unknown",
  },
  pageContext: {
    route: "/",
    pageType: "unknown",
    userPermissions: [],
    availableActions: [],
    suggestedActions: [],
  },
  selectedElements: [],
  quickActions: [],
  notifications: [],
  preferences: defaultPreferences,
};

// Reducer
function solaFloatingReducer(state, action) {
  switch (action.type) {
    case "OPEN_WINDOW":
      return { ...state, isOpen: true, isMinimized: false };

    case "CLOSE_WINDOW":
      return {
        ...state,
        isOpen: false,
        isMinimized: false,
        isMaximized: false,
      };

    case "MINIMIZE_WINDOW":
      return { ...state, isMinimized: true, isMaximized: false };

    case "MAXIMIZE_WINDOW":
      return { ...state, isMaximized: true, isMinimized: false };

    case "RESTORE_WINDOW":
      return { ...state, isMaximized: false, isMinimized: false };

    case "SET_POSITION":
      return {
        ...state,
        position: { ...state.position, ...action.payload },
      };

    case "SET_SIZE":
      return {
        ...state,
        size: { ...state.size, ...action.payload },
      };

    case "SET_TRANSPORT":
      return {
        ...state,
        transport: action.payload,
      };

    case "SET_DRAGGING":
      return { ...state, isDragging: action.payload };

    case "SET_RESIZING":
      return { ...state, isResizing: action.payload };

    case "UPDATE_PAGE_CONTEXT":
      return {
        ...state,
        pageContext: { ...state.pageContext, ...action.payload },
        currentPage: {
          ...state.currentPage,
          route: action.payload.route || state.currentPage.route,
          pageType: action.payload.pageType || state.currentPage.pageType,
          entityId: action.payload.entityId || state.currentPage.entityId,
          entityData: action.payload.entityData || state.currentPage.entityData,
        },
      };

    case "SET_SELECTED_ELEMENTS":
      return { ...state, selectedElements: action.payload };

    case "ADD_NOTIFICATION":
      return {
        ...state,
        notifications: [action.payload, ...state.notifications].slice(0, 20), // Max 20 notifications
      };

    case "REMOVE_NOTIFICATION":
      return {
        ...state,
        notifications: state.notifications.filter(
          (notif) => notif.id !== action.payload
        ),
      };

    case "MARK_NOTIFICATION_READ":
      return {
        ...state,
        notifications: state.notifications.map((notif) =>
          notif.id === action.payload ? { ...notif, read: true } : notif
        ),
      };

    case "UPDATE_PREFERENCES":
      return {
        ...state,
        preferences: { ...state.preferences, ...action.payload },
      };

    case "ADD_QUICK_ACTION":
      return {
        ...state,
        quickActions: [
          ...state.quickActions.filter((qa) => qa.id !== action.payload.id),
          action.payload,
        ],
      };

    case "REMOVE_QUICK_ACTION":
      return {
        ...state,
        quickActions: state.quickActions.filter(
          (qa) => qa.id !== action.payload
        ),
      };

    case "LOAD_PERSISTED_STATE":
      return { ...state, ...action.payload };

    default:
      return state;
  }
}

// Context
const SOLAFloatingContext = createContext(null);

// Provider component
export function SOLAFloatingProvider({ children }) {
  const [state, dispatch] = useReducer(solaFloatingReducer, defaultState);

  const router = useRouter();
  const pathname = usePathname();

  // Load persisted state on mount (client-side only)
  useEffect(() => {
    // Guard against SSR - localStorage doesn't exist on server
    if (typeof window === "undefined") return;

    try {
      const savedState = localStorage.getItem("sola-floating-state");

      if (savedState) {
        const parsedState = JSON.parse(savedState);
        // Always start with window closed for better UX
        dispatch({
          type: "LOAD_PERSISTED_STATE",
          payload: {
            ...parsedState,
            isOpen: false,
            isMinimized: false,
          }
        });
      }
    } catch (error) {
      // Fails gracefully in Safari private mode or when localStorage is unavailable
      console.warn("Failed to load SOLA floating state:", error);
    }
  }, []);

  // Persist state changes (optimized with debouncing and selective saving)
  useEffect(() => {
    // Guard against SSR - localStorage doesn't exist on server
    if (typeof window === "undefined") return;

    // Debounce position/size changes - only save after 500ms of no changes
    const timeoutId = setTimeout(() => {
      const stateToSave = {
        position: state.position,
        size: state.size,
        preferences: state.preferences,
        isOpen: state.isOpen,
        isMinimized: state.isMinimized,
      };

      try {
        localStorage.setItem("sola-floating-state", JSON.stringify(stateToSave));
      } catch (err) {
        // Fail silently if localStorage is full or unavailable
        console.warn('[SOLA Context] Failed to save state to localStorage:', err);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [state.position, state.size, state.preferences, state.isOpen, state.isMinimized]);

  // Update page context when route changes
  useEffect(() => {
    const pageType = detectPageType(pathname);
    const entityId = extractEntityId(pathname, pageType);

    dispatch({
      type: "UPDATE_PAGE_CONTEXT",
      payload: {
        route: pathname,
        pageType,
        entityId,
        availableActions: getAvailableActions(pageType),
        suggestedActions: getSuggestedActions(pageType, entityId),
      },
    });
  }, [pathname]);

  // Window controls
  const openWindow = useCallback(() => {
    dispatch({ type: "OPEN_WINDOW" });
  }, []);
  const closeWindow = useCallback(() => dispatch({ type: "CLOSE_WINDOW" }), []);
  const minimizeWindow = useCallback(
    () => dispatch({ type: "MINIMIZE_WINDOW" }),
    []
  );
  const maximizeWindow = useCallback(
    () => dispatch({ type: "MAXIMIZE_WINDOW" }),
    []
  );
  const restoreWindow = useCallback(
    () => dispatch({ type: "RESTORE_WINDOW" }),
    []
  );

  const setPosition = useCallback((position) => {
    dispatch({ type: "SET_POSITION", payload: position });
  }, []);

  const setSize = useCallback((size) => {
    dispatch({ type: "SET_SIZE", payload: size });
  }, []);

  const setTransport = useCallback((transport) => {
    dispatch({ type: "SET_TRANSPORT", payload: transport });
  }, []);

  // Context management
  const updatePageContext = useCallback((context) => {
    dispatch({ type: "UPDATE_PAGE_CONTEXT", payload: context });
  }, []);

  const setSelectedElements = useCallback((elements) => {
    dispatch({ type: "SET_SELECTED_ELEMENTS", payload: elements });
  }, []);

  // Notification management
  const addNotification = useCallback((notification) => {
    // Use crypto.randomUUID for stable unique IDs (client-side only operation)
    const uniqueId = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `notif-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const fullNotification = {
      ...notification,
      id: uniqueId,
      timestamp: new Date(),
      read: false,
    };
    dispatch({ type: "ADD_NOTIFICATION", payload: fullNotification });
  }, []);

  const removeNotification = useCallback((notificationId) => {
    dispatch({ type: "REMOVE_NOTIFICATION", payload: notificationId });
  }, []);

  const markNotificationRead = useCallback((notificationId) => {
    dispatch({ type: "MARK_NOTIFICATION_READ", payload: notificationId });
  }, []);

  // Preferences
  const updatePreferences = useCallback((preferences) => {
    dispatch({ type: "UPDATE_PREFERENCES", payload: preferences });
  }, []);

  // Quick actions
  const executeQuickAction = useCallback(
    async (actionId) => {
      const action = state.quickActions.find((qa) => qa.id === actionId);
      if (action) {
        try {
          await action.execute();
        } catch (error) {
          console.error("Failed to execute quick action:", error);
          addNotification({
            type: "error",
            title: "Action Failed",
            message: `Failed to execute ${action.label}`,
          });
        }
      }
    },
    [state.quickActions, addNotification]
  );

  const addQuickAction = useCallback((action) => {
    dispatch({ type: "ADD_QUICK_ACTION", payload: action });
  }, []);

  const removeQuickAction = useCallback((actionId) => {
    dispatch({ type: "REMOVE_QUICK_ACTION", payload: actionId });
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    // Guard against SSR
    if (typeof window === "undefined") return;
    if (!state.preferences.keyboardShortcuts) return;

    const handleKeydown = (e) => {
      // Ctrl/Cmd + Shift + S = Toggle SOLA
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "S") {
        e.preventDefault();
        if (state.isOpen) {
          closeWindow();
        } else {
          openWindow();
        }
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [
    state.preferences.keyboardShortcuts,
    state.isOpen,
    openWindow,
    closeWindow,
  ]);

  const contextValue = {
    state,
    openWindow,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    restoreWindow,
    setPosition,
    setSize,
    setTransport,
    updatePageContext,
    setSelectedElements,
    addNotification,
    removeNotification,
    markNotificationRead,
    updatePreferences,
    executeQuickAction,
    addQuickAction,
    removeQuickAction,
  };

  return (
    <SOLAFloatingContext.Provider value={contextValue}>
      {children}
    </SOLAFloatingContext.Provider>
  );
}

// Hook
export function useSOLAFloating() {
  const context = useContext(SOLAFloatingContext);
  if (!context) {
    throw new Error(
      "useSOLAFloating must be used within a SOLAFloatingProvider"
    );
  }
  return context;
}

// Utility functions
function detectPageType(pathname) {
  if (pathname.includes("/candidates/")) return "candidate";
  if (pathname.includes("/companies/")) return "company";
  if (pathname.includes("/jobs/")) return "job";
  if (pathname.includes("/dashboard")) return "dashboard";
  if (pathname.includes("/reports")) return "reports";
  if (pathname.includes("/settings")) return "settings";
  return "unknown";
}

function extractEntityId(pathname, pageType) {
  const segments = pathname.split("/");

  switch (pageType) {
    case "candidate":
      const candidateIndex = segments.indexOf("candidates");
      return candidateIndex !== -1 ? segments[candidateIndex + 1] : undefined;
    case "company":
      const companyIndex = segments.indexOf("companies");
      return companyIndex !== -1 ? segments[companyIndex + 1] : undefined;
    case "job":
      const jobIndex = segments.indexOf("jobs");
      return jobIndex !== -1 ? segments[jobIndex + 1] : undefined;
    default:
      return undefined;
  }
}

function getAvailableActions(pageType) {
  // This will be expanded with actual actions
  const baseActions = [
    { id: "help", label: "Get Help", category: "general" },
    { id: "export", label: "Export Data", category: "data" },
  ];

  switch (pageType) {
    case "candidate":
      return [
        ...baseActions,
        {
          id: "translate-profile",
          label: "Translate Profile",
          category: "localization",
        },
        {
          id: "generate-interview",
          label: "Generate Interview Questions",
          category: "automation",
        },
        {
          id: "find-similar",
          label: "Find Similar Candidates",
          category: "analysis",
        },
      ];
    case "job":
      return [
        ...baseActions,
        {
          id: "find-candidates",
          label: "Find Matching Candidates",
          category: "analysis",
        },
        {
          id: "generate-description",
          label: "Generate Job Description",
          category: "automation",
        },
      ];
    default:
      return baseActions;
  }
}

function getSuggestedActions(pageType, entityId) {
  // This will be expanded with intelligent suggestions
  return [];
}
